import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Stats } from '@react-three/drei';
import { NoToneMapping, Vector3 } from 'three';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import useGetCamera from './controls/Camera';
import { useFloorContext } from './context/FloorContext';
import FloorController from './components/FloorController';
import { CELL_SIZE } from './constants';
import UIController from './components/UI/UIController';
import { getWorldPositionFromNFT } from './utils/floor_utils';
import { RoomObjectType } from './types/room';
import { useNFTContext } from './context/NFTContext';
import { useTargetContext } from './context/TargetContext';

const Main = () => {
  const { target } = useTargetContext();
  const { currentFloorMap, currentFloor, isFetching } = useFloorContext();
  const { selectedNFT } = useNFTContext();

  const camera = useGetCamera();
  const cameraControls = useRef<any>(null);

  const minCameraZoom = useMemo<number>(() => {
    if (currentFloorMap)
      return Math.max(0.01 * (100 - currentFloorMap?.width), 0.33);
    return 0.33;
  }, [currentFloorMap]);

  const maxCameraZoom = useMemo<number>(() => {
    return 4;
  }, []);

  const cameraZoomStep = useMemo<number>(() => {
    return (maxCameraZoom - minCameraZoom) / 3;
  }, [minCameraZoom, maxCameraZoom]);

  const middleOfTheFloor = useMemo<Vector3>(() => {
    return new Vector3(
      -((currentFloorMap?.width ?? 0) / 2) * CELL_SIZE,
      0,
      ((currentFloorMap?.height ?? 0) / 2) * CELL_SIZE
    );
  }, [currentFloorMap]);

  const [lastFoundNFT, setLastFoundNFT] = useState<RoomObjectType | null>(null);
  const [nftTargetWorldPosition, setNftTargetWorldPosition] =
    useState<Vector3 | null>(null);

  const currentCameraTarget = useMemo<Vector3 | undefined>(() => {
    if (!selectedNFT) return middleOfTheFloor;

    if (
      nftTargetWorldPosition &&
      lastFoundNFT?.roomId === selectedNFT?.roomId
    ) {
      return nftTargetWorldPosition;
    } else {
      return middleOfTheFloor;
    }
  }, [
    selectedNFT,
    middleOfTheFloor,
    nftTargetWorldPosition,
    lastFoundNFT?.roomId,
  ]);

  const setCameraZoom = useCallback(
    (zoom: number) => {
      if (cameraControls && cameraControls.current) {
        cameraControls.current.object.zoom = zoom;
        camera.updateProjectionMatrix();
      }
    },
    [camera]
  );

  const cameraMinusZoom = useCallback(() => {
    setCameraZoom(
      Math.min(
        Math.max(
          cameraControls.current.object.zoom - cameraZoomStep,
          minCameraZoom
        ),
        maxCameraZoom
      )
    );
  }, [cameraZoomStep, maxCameraZoom, minCameraZoom, setCameraZoom]);

  const cameraPlusZoom = useCallback(() => {
    setCameraZoom(
      Math.min(
        Math.max(
          cameraControls.current.object.zoom + cameraZoomStep,
          minCameraZoom
        ),
        maxCameraZoom
      )
    );
  }, [cameraZoomStep, maxCameraZoom, minCameraZoom, setCameraZoom]);

  // const setCameraTarget = useCallback(
  //   (x: number, z: number) => {
  //     if (cameraControls.current) {
  //       target === 'dev' && console.log('SET CAMERA TARGET ' + x + ' ' + z);
  //       cameraControls.current.target.x = x;
  //       cameraControls.current.target.z = z;
  //     }
  //   },
  //   [cameraControls]
  // );

  const bringCameraToNFT = useCallback(
    (nft: RoomObjectType) => {
      if (
        cameraControls &&
        cameraControls.current &&
        nft.endX > -1 &&
        nft.startX > -1 &&
        nft.endY > -1 &&
        nft.startY > -1
      ) {
        setLastFoundNFT(nft);

        const amountOfBoxesX = nft.endX - nft.startX + 1;
        const amountOfBoxesY = nft.endY - nft.startY + 1;

        const worldPosition = getWorldPositionFromNFT(
          nft,
          CELL_SIZE,
          amountOfBoxesX,
          amountOfBoxesY
        );

        if (worldPosition) {
          target === 'dev' &&
            console.log(
              'bring camera to NFT: ' + worldPosition.x + ' ' + worldPosition.z
            );

          setNftTargetWorldPosition(
            new Vector3(worldPosition.x, worldPosition.y, worldPosition.z)
          );
          setCameraZoom(maxCameraZoom);
        }
      }
    },
    [maxCameraZoom, setCameraZoom, target]
  );

  useEffect(() => {
    if (!selectedNFT) {
      setNftTargetWorldPosition(null);
      setCameraZoom(minCameraZoom);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFloor]);

  const [showLandingPage, setShowLandingPage] = useState(true);

  return (
    <div className="main-container">
      {isFetching && (
        <div className="loading-icon-blackout">
          <AiOutlineLoading3Quarters
            color="white"
            size={40}
            className="loading-icon"
          />
        </div>
      )}
      <UIController
        bringCameraToNFT={bringCameraToNFT}
        cameraPlusZoom={cameraPlusZoom}
        cameraMinusZoom={cameraMinusZoom}
        showLandingPage={showLandingPage}
        setShowLandingPage={setShowLandingPage}
      />
      <Canvas
        gl={{
          antialias: true,
          toneMapping: NoToneMapping,
        }}
        camera={camera}
        flat
        linear
      >
        <color attach="background" args={['#1E1E1E']} />
        <ambientLight intensity={0.8} />
        <directionalLight color="white" position={[-5, 5, 5]} />
        <FloorController setShowLandingPage={setShowLandingPage} />
        <OrbitControls
          ref={cameraControls}
          enableRotate={false}
          enablePan={true}
          enableZoom={true}
          minPolarAngle={Math.PI / 3}
          maxPolarAngle={Math.PI / 3}
          minAzimuthAngle={Math.PI * 1.25}
          maxAzimuthAngle={Math.PI * 1.25}
          minZoom={minCameraZoom}
          maxZoom={maxCameraZoom}
          zoomSpeed={0.75}
          panSpeed={1.5}
          target={currentCameraTarget}
        />
        {target === 'dev' && <Stats />}
      </Canvas>
      <canvas
        id="myCanvas"
        width="1024"
        height="768"
        style={{
          position: 'relative',
          background: 'black',
          display: 'none',
        }}
      ></canvas>
    </div>
  );
};

export default Main;
